import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { splitSheetStyles } from "../../components/Modal/styles";
import {
  Box,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  Breadcrumbs,
  Link,
} from "@mui/material";
import {
  a11yProps,
  CustomTabPanel,
} from "../../components/TracksPageComponents/CustomTabPanel";
import { FolderSection } from "../../components/TracksPageComponents/FolderSection";
import {
  DownloadProjectOrFolderResponse,
  Folder,
  GetFoldersResponse,
  GetProjectFilesResponse,
  GetShareAccessTokenResponse,
  GetUserResponse,
  ProjectFile as ProjectFileType,
  Share,
  ShareAnalytics,
  ShareType,
  ShareTypeMap,
  Track,
} from "../../proto/api_pb";
import {
  CustomTracksListContainer,
  CustomTracksScrollingContainer,
} from "../Tracks/styles";
import { TracksTableHeadings } from "../../components/TracksPageComponents/TracksTableHeadings";
import {
  AuthContext,
  UserDetailsContext,
  UserDetailsContextInterface,
} from "../../contexts";
import { styles } from "../Tracks/styles";
import useInfiniteScroll from "../../hooks/useInfiniteScroll";
import {
  TrackFilterParams,
  TrackObj,
  TrackVersionsObj,
} from "../MyTracks/types";
import { TrackListView } from "../../components/TracksPageComponents/TrackListView";
import { useParams } from "react-router-dom";
import {
  AddShareAnalyticsEvent,
  getShareAccessToken,
  increaseShareUrlViews,
} from "../../services/shareService";
import { getPublicUser } from "../../services/userService";
import { getAllFolders } from "../../services/folderService";
import { getTracks } from "../Tracks/tracksHandler";
import { downloadProject, getAllProjectFiles } from "../../services/projectService";
import { ProjectFile as ProjectFileClass } from "../../proto/api_pb";
import { AudioPlayer } from "../../components/TracksPageComponents/AudioPlayer";
import { TrackToPlay } from "../ExplorePage/types";
import { AudioDetails } from "../../components/TracksPageComponents/types";
import { ProjectFile } from "../../components/SingleProject/ProjectFile";
import { projectPageStyles } from "../Projects/styles";
import { BORDER_COLOR, TEXT_COLOR } from "../../theme";
import { RpcError } from "grpc-web";
import { toast } from "react-toastify";
import JSZip from "jszip";
import CustomButton from "../../components/CustomButton/CustomButton";
import DownloadIcon from "@mui/icons-material/Download";

interface BreadcrumbItem {
  id: string;
  name: string;
}

interface SharedFolderPageProps {
  isUserLoading: boolean;
}

export const SharedFolderPage: React.FC<SharedFolderPageProps> = ({
  isUserLoading,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [share, setShare] = useState<Share>();
  const [shareToken, setShareToken] = useState<string>();
  const [shareType, setShareType] =
    useState<ShareTypeMap[keyof ShareTypeMap]>();
  const [shareObjectId, setShareObjectId] = useState<string>();
  const [unlimitedViews, setUnlimitedViews] = useState<boolean>();
  const [allowDownload, setIsAllowDownload] = useState<boolean>();
  const [ownerDetails, setOwnerDetails] = useState<GetUserResponse>();
  const [isMaxViewsReached, setIsMaxViewsReached] = useState<boolean>(false);
  const { id, name } = useParams();
  const [value, setValue] = useState(0);
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadingRef = useRef<HTMLDivElement | null>(null);
  const [isAllTracksSelected, setIsAllTracksSelected] =
    useState<boolean>(false);
  const [tracks, setTracks] = useState<TrackObj[]>([]);
  const { isEnterpriseAccount } = useContext(UserDetailsContext);
  const { userId } = useContext(AuthContext);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [projectFiles, setProjectFiles] = useState<ProjectFileClass[]>([]);
  const [trackToPlay, setTrackToPlay] = useState<TrackToPlay>();
  const [isPlayButtonClicked, setIsPlayButtonClicked] =
    useState<boolean>(false);
  const [versionToPlay, setVersionToPlay] = useState<TrackVersionsObj>();
  const [audioDetails, setAudioDetails] = useState<AudioDetails>();
  const [isTrackForPlay, setIsTrackForPlay] = useState<boolean>(true);
  const [selectedTrackLatestVersion, setSelectedTrackLatestVersion] =
    useState(0);
  const [selectedTrack, setSelectedTrack] = useState<Track>();
  const [versionModalIsOpen, setVersionModalIsOpen] = useState(false);
  const [trackToExpand, setTrackToExpand] = useState("");
  const { userName } =
    useContext<UserDetailsContextInterface>(UserDetailsContext);

  const [selectedFolderId, setSelectedFolderId] = useState<string>(() => {
    try {
      const saved = sessionStorage.getItem(`selectedFolderId_${id}`);
      return saved ? JSON.parse(saved) : "";
    } catch {
      return "";
    }
  });

  const [breadcrumbPath, setBreadcrumbPath] = useState<BreadcrumbItem[]>(() => {
    try {
      const saved = sessionStorage.getItem(`breadcrumbPath_${id}`);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const {
    page,
    scrollContainerRef,
    setIsScrollEnabled,
    isScrollEnabled,
    setPage,
  } = useInfiniteScroll();
  const isProjectShared = window.location.pathname.includes("/share/project/");
  const isSharePage = window.location.pathname.includes("/share/");
  const filterAndSortParams = useMemo<TrackFilterParams>(
    () => ({
      userIds: [],
      sortBy: "Last modified",
      isAscending: false,
      genreList: [],
      artistList: [],
      collaboratorsList: [],
      TrackPrivacy: "All",
      page: 1,
      limit: 10,
      bpmStartValue: 0,
      bpmEndValue: 200,
      status: "",
      isCompletedTracks: value === 2,
    }),
    [value]
  );

  const isAddFolderToProject = true;

  const handleChange = async (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setTracks([]);
    setProjectFiles([]);
    setPage(0);
    setValue(newValue);
    sessionStorage.setItem("TabValue", JSON.stringify(newValue));
  };

  const handleExpandTrack = (trackId: string) => {
    setTrackToExpand((prevId) => (prevId === trackId ? "" : trackId));
  };

  const handleClosePlayerForModal = () => {
    setIsPlayButtonClicked(false);
    setIsTrackForPlay(false);
    setTrackToPlay(undefined);
  };

  const handleOpenTrackVersionModal = (
    tr: Track,
    latestVersionNumber: number
  ) => {
    handleClosePlayerForModal();
    setSelectedTrackLatestVersion(latestVersionNumber);
    setSelectedTrack(tr);
    setVersionModalIsOpen(true);
  };

  useEffect(() => {
    sessionStorage.setItem(
      `breadcrumbPath_${id}`,
      JSON.stringify(breadcrumbPath)
    );
  }, [breadcrumbPath, id]);

  useEffect(() => {
    sessionStorage.setItem(
      `selectedFolderId_${id}`,
      JSON.stringify(selectedFolderId)
    );
  }, [selectedFolderId, id]);

  const updateBreadcrumbPath = useCallback(
    (folderId: string, folderName: string) => {
      setBreadcrumbPath((prev) => {
        const existingIndex = prev.findIndex((item) => item.id === folderId);
        if (existingIndex !== -1) {
          return prev.slice(0, existingIndex + 1);
        } else {
          return [...prev, { id: folderId, name: folderName }];
        }
      });
    },
    []
  );


  useEffect(() => {
    return () => {
      sessionStorage.removeItem(`selectedFolderId_${id}`);
      sessionStorage.removeItem(`breadcrumbPath_${id}`);
    };
  }, [id]);

  useEffect(() => {
    setLoading(true);
    const helper = async () => {
      if (id) {
        const res = await getShareAccessToken(id);
        console.log("res :", res);
        if (res instanceof GetShareAccessTokenResponse) {
          const ownerId: string = res.getShare()?.getOwnerId() ?? "";
          setIsAllowDownload(res.getShare()?.getAllowDownload());
          setShare(res.getShare());
          setShareToken(res.getAccessToken());
          setShareType(res.getShare()?.getShareType());
          setShareObjectId(res.getShare()?.getObjectId());
          setUnlimitedViews(res.getShare()?.getUnlimitedViews());
          setLoading(false);

          const getUserResponse = await getPublicUser(
            res.getAccessToken(),
            ownerId
          );
          if (getUserResponse instanceof GetUserResponse) {
            setOwnerDetails(getUserResponse);
          }
        } else {
          if (res === undefined) {
            setIsMaxViewsReached(true);
            setIsLoading(false);
          }
        }
      }
    };
    helper();
  }, [id]);

  const getAllFoldersForProject = useCallback(
    async (folderId?: string) => {
      if (!shareToken) return;
      const targetId = folderId || selectedFolderId || share?.getObjectId();
      const res = await getAllFolders(
        shareToken,
        userId,
        isProjectShared && breadcrumbPath.length === 0 ? targetId ?? "" : "",
        isProjectShared && breadcrumbPath.length === 0 ? "" : targetId,
        "",
        true
      );
      if (res instanceof GetFoldersResponse) {
        setFolders(res.getFoldersList());
      }
    },
    [
      shareToken,
      selectedFolderId,
      share,
      userId,
      isProjectShared,
      breadcrumbPath.length,
    ]
  );

  const handleFetchTracks = useCallback(
    async (folderId?: string) => {
      if (value === 1) return;
      if (!shareToken) return;
      setIsLoading(true);
      // setTracks([]);
      const targetId = folderId || selectedFolderId || share?.getObjectId();
      await getTracks(
        "",
        filterAndSortParams,
        shareToken,
        setTracks,
        page,
        setIsScrollEnabled,
        "",
        setIsLoading,
        isProjectShared && breadcrumbPath.length === 0 ? targetId : "",
        "",
        2,
        value === 2,
        isProjectShared && breadcrumbPath.length === 0 ? "" : targetId
      );
      setIsLoading(false);
    },
    [
      filterAndSortParams,
      isProjectShared,
      page,
      selectedFolderId,
      setIsScrollEnabled,
      share,
      shareToken,
      value,
      breadcrumbPath.length,
    ]
  );

  const getProjectFiles = useCallback(
    async (folderId?: string) => {
      if (!shareToken) return;
      const targetId = folderId || selectedFolderId || share?.getObjectId();
      const res = await getAllProjectFiles(
        shareToken,
        isProjectShared && breadcrumbPath.length === 0 ? targetId ?? "" : "",
        "",
        "",
        "",
        page,
        10,
        true,
        0,
        true,
        isProjectShared && breadcrumbPath.length === 0 ? "" : targetId
      );
      if (res instanceof GetProjectFilesResponse) {
        setProjectFiles((prev) => {
          const existingFileIds = new Set(prev.map((file) => file.getId()));
          const newFiles = res
            .getProjectFilesList()
            .filter((file) => !existingFileIds.has(file.getId()));
          return [...prev, ...newFiles];
        });
        setIsLoading(false);
        if (res.getProjectFilesList().length < 10) {
          setIsScrollEnabled(false);
        } else {
          setIsScrollEnabled(true);
        }
      }
      setIsLoading(false);
    },
    [
      shareToken,
      selectedFolderId,
      share,
      isProjectShared,
      page,
      setIsScrollEnabled,
      breadcrumbPath.length,
    ]
  );
  const handleRootClick = useCallback(() => {
    const rootId = share?.getObjectId() || "";
    setSelectedFolderId(rootId);
    getAllFoldersForProject(rootId);
    handleFetchTracks(rootId);
    getProjectFiles(rootId);
    setBreadcrumbPath([]);
  }, [getAllFoldersForProject, getProjectFiles, handleFetchTracks, share]);

  const handleBreadcrumbClick = useCallback((folderId: string) => {
    setSelectedFolderId(folderId);
    setPage(0);
    getAllFoldersForProject(folderId);
    if (value !== 1) {
      handleFetchTracks(folderId);
    }
    if (value === 1) {
      getProjectFiles(folderId);
    }
    setBreadcrumbPath((prev) => {
      const clickedIndex = prev.findIndex((item) => item.id === folderId);
      return prev.slice(0, clickedIndex + 1);
    });
  }, [getAllFoldersForProject, getProjectFiles, handleFetchTracks, setPage, value]);

  useEffect(() => {
    if (value === 1) {
      getProjectFiles();
    } else {
      handleFetchTracks();
    }
    getAllFoldersForProject();
  }, [
    getAllFoldersForProject,
    getProjectFiles,
    handleFetchTracks,
    value,
    page,
  ]);

  const handleFolderClick = useCallback(
    (folderId: string, folderName: string) => {
      setTracks([])
      setProjectFiles([])
      setPage(0);
      setSelectedFolderId(folderId);
      getAllFoldersForProject(folderId);
      handleFetchTracks(folderId);
      getProjectFiles(folderId);
      updateBreadcrumbPath(folderId, folderName);
    },
    [setPage, getAllFoldersForProject, handleFetchTracks, getProjectFiles, updateBreadcrumbPath]
  );

  const handleAddShareEvent = useCallback(async () => {
    if (!share || !shareToken || isUserLoading) return;
    await increaseShareUrlViews(share?.getId(), shareToken);
    const sh = new ShareAnalytics();
    sh.setShareId(share?.getId());
    sh.setType("VIEW");
    const response = await fetch("https://ipapi.co/json/");
    if (response.ok) {
      const data = await response.json();
      sh.setIp(data.ip);
      sh.setCountry(data.country_name);
    }
    sh.setTimestamp(Date.now());
    sh.setOmmuseUsername(userName);
    const res = await AddShareAnalyticsEvent(shareToken ?? "", sh);
    if (res instanceof RpcError) toast.warn("Failed to update analytics event");
  }, [isUserLoading, share, shareToken, userName]);

  useEffect(() => {
    const handleUseEffect = async () => {
      if (!share || !shareToken) return;
      await handleAddShareEvent();
    };
    handleUseEffect();
  }, [handleAddShareEvent, share, shareToken]);

  const handleDownloadProjectAndFolder = async () => {
    if (!shareToken) return

    try {
      setLoading(true);

      if (share && shareToken && !isUserLoading) {
        const sh = new ShareAnalytics();
        sh.setShareId(share?.getId());
        sh.setType("DOWNLOAD");
        const response = await fetch("https://ipapi.co/json/");
        if (response.ok) {
          const data = await response.json();
          sh.setIp(data.ip);
          sh.setCountry(data.country_name);
        }
        sh.setTimestamp(Date.now());
        sh.setOmmuseUsername(userName);
        const analyticsRes = await AddShareAnalyticsEvent(shareToken ?? "", sh);
        if (analyticsRes instanceof RpcError) toast.warn("Failed to update analytics event");
      }

      const res = await downloadProject(
        shareToken,
        "",
        "",
        share?.getShareType() === ShareType.SHARE_TYPE_PROJECT ? share?.getObjectId() : "",
        share?.getShareType() === ShareType.SHARE_TYPE_FOLDER ? share?.getObjectId() : "",
      );

      if (res instanceof DownloadProjectOrFolderResponse) {
        const projectFiles = res.getProjectFilesList();
        const trackFiles = res.getTracksList();

        if (!projectFiles.length && !trackFiles.length) {
          toast.warn(`No files or tracks found in the ${isProjectShared ? "project" : "folder"}`);
          setLoading(false);
          return;
        }

        const zip = new JSZip();

        const projectFilePromises = projectFiles.map(
          async (file: ProjectFileType) => {
            const fileUrl = file.getFileUrl();
            const fileName = file.getFileName();
            if (fileUrl) {
              try {
                const response = await fetch(fileUrl);
                if (!response.ok)
                  throw new Error(`Failed to fetch file: ${fileName}`);
                const fileBlob = await response.blob();
                zip.file(`Files/${fileName}`, fileBlob);
              } catch (error) {
                console.error(`Error downloading file ${fileName}:`, error);
              }
            }
          }
        );

        const trackFilePromises = trackFiles.map(async (trackResponse) => {
          const trackVersion = trackResponse.getLatestTrackVersion();
          const track = trackResponse.getTrack();

          if (!trackVersion) {
            console.error("Track version is undefined for track:");
            return;
          }

          const trackUrl = trackVersion.getTrackUrl();

          let fileExtension = ".mp3";
          if (trackUrl) {
            const urlParts = trackUrl.split(".");
            if (urlParts.length > 1) {
              fileExtension = `.${urlParts[urlParts.length - 1].split("?")[0]}`;
            }
          }

          const trackName = `${(track?.getTitle() || "Unknown").substring(
            0,
            30
          )}${fileExtension}`;

          if (trackUrl) {
            try {
              const response = await fetch(trackUrl);
              if (!response.ok)
                throw new Error(`Failed to fetch track: ${trackName}`);
              const trackBlob = await response.blob();

              if (response.headers.has("content-type")) {
                const contentType = response.headers.get("content-type");
                if (contentType) {
                  const mimeToExt = {
                    "audio/mpeg": ".mp3",
                    "audio/wav": ".wav",
                    "audio/x-wav": ".wav",
                    "audio/ogg": ".ogg",
                    "audio/flac": ".flac",
                    "audio/aac": ".aac",
                    "audio/mp4": ".m4a",
                    // Add more MIME types as needed
                  };

                  for (const [mime, ext] of Object.entries(mimeToExt)) {
                    if (contentType.includes(mime)) {
                      fileExtension = ext;
                      break;
                    }
                  }
                }
              }

              const finalTrackName = `${(
                track?.getTitle() || "Unknown"
              ).substring(0, 50)}${fileExtension}`;
              zip.file(`Tracks/${finalTrackName}`, trackBlob);
            } catch (error) {
              console.error(`Error downloading track ${trackName}:`, error);
            }
          } else {
            console.error(`No URL found for track: ${trackName}`);
          }
        });

        await Promise.all([...projectFilePromises, ...trackFilePromises]);

        const zipBlob = await zip.generateAsync({ type: "blob" });
        const url = URL.createObjectURL(zipBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${share?.getTrackTitle()}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(`${isProjectShared ? "project" : "folder"} is Ready to download!`);
      } else {
        console.error("Failed to download project", res);
      }
    } catch (error) {
      console.error("Error downloading project:", error);
      toast.error(`Failed to download ${isProjectShared ? "project" : "folder"}`);
    } finally {
      setLoading(false);
    }
  };

  if (isMaxViewsReached) {
    return (
      <Box
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: '50px'
        }}
      >
        <h1 style={{ color: "white" }}>{"Views limit is over!"}</h1>
      </Box>
    );
  }

  if (share && share.getExpiryTimestamp() < Date.now()) {
    return (
      <Box
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: '50px'
        }}
      >
        <h1 style={{ color: "white" }}>{"Link expired!"}</h1>
      </Box>
    );
  }
  return (
    <div className="px-5 py-2">
      {share && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: isSmallScreen ? 'column' : 'row' }}>
            <h2 className="text-2xl font-bold mb-1">
              {isProjectShared ? "Project" : "Folder"} {name} shared with you.
            </h2>
            {allowDownload && breadcrumbPath.length === 0 &&
              <CustomButton
                text={`Download ${isProjectShared ? "Project" : "Folder"}`}
                icon={DownloadIcon}
                onClick={handleDownloadProjectAndFolder}
                customStyles={{ width: "170px" }}
                loading={loading}
              />}
          </Box>
          <Box sx={{ mb: 1 }}>
            <Breadcrumbs
              aria-label="folder navigation"
              sx={{
                "& .MuiBreadcrumbs-separator": {
                  color: "text.secondary",
                },
                "& .MuiBreadcrumbs-li": {
                  display: "flex",
                  alignItems: "center",
                },
              }}
            >
              <Link
                component="button"
                variant="body2"
                onClick={handleRootClick}
                sx={{
                  textDecoration: "none",
                  color: TEXT_COLOR,
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                  background: "none",
                  border: "none",
                  padding: 0,
                  font: "inherit",
                }}
              >
                {name}
              </Link>
              {breadcrumbPath.map((item, index) => (
                <Link
                  key={item.id}
                  component="button"
                  variant="body2"
                  onClick={() => handleBreadcrumbClick(item.id)}
                  sx={{
                    textDecoration: "none",
                    color: TEXT_COLOR,
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration:
                        index === breadcrumbPath.length - 1
                          ? "none"
                          : "underline",
                    },
                    background: "none",
                    border: "none",
                    padding: 0,
                    font: "inherit",
                  }}
                >
                  {item.name}
                </Link>
              ))}
            </Breadcrumbs>
          </Box>
          <FolderSection
            folders={folders}
            isLoading={isLoading}
            setOpenFoldersList={undefined}
            setTracks={() => { }}
            setValue={setValue}
            value={value}
            getAllFoldersForProject={getAllFoldersForProject}
            selectedFolders={[]}
            setSelectedFolders={() => { }}
            isAddFolderToProject={true}
            onFolderClick={(folderId: string, folderName?: string) => {
              const folderToClick = folders.find((f) => f.getId() === folderId);
              const displayName =
                folderName || folderToClick?.getName() || "Unknown Folder";
              handleFolderClick(folderId, displayName);
              setPage(0);
            }}
          />
          <Box sx={{ width: "100%", height: '40px' }}>
            <Tabs
              TabIndicatorProps={{
                style: { display: "none" },
              }}
              onChange={handleChange}
              value={value}
              aria-label="basic tabs example"
              sx={{
                display: "flex",
                flexWrap: "wrap",
              }}
            >
              <Tab
                sx={{
                  ...splitSheetStyles.tab,
                  flex: isSmallScreen ? "1 1 50%" : "",
                  background:
                    value === 0
                      ? "var(--Colors-Background-bg-active, #202425)"
                      : undefined,
                }}
                label="Tracks"
                {...a11yProps(0)}
              />
              <Tab
                sx={{
                  ...splitSheetStyles.tab,
                  flex: isSmallScreen ? "1 1 50%" : "",
                  background:
                    value === 1
                      ? "var(--Colors-Background-bg-active, #202425)"
                      : undefined,
                }}
                label="Files"
                {...a11yProps(1)}
              />
              <Tab
                sx={{
                  ...splitSheetStyles.tab,
                  flex: isSmallScreen ? "1 1 60%" : "",
                  background:
                    value === 2
                      ? "var(--Colors-Background-bg-active, #202425)"
                      : undefined,
                }}
                label="Completed Tracks"
                {...a11yProps(2)}
              />
            </Tabs>
          </Box>
          <CustomTabPanel value={value} index={0}>
            <CustomTracksListContainer>
              <TracksTableHeadings
                filterAndSortParams={filterAndSortParams}
                handleAscendingOrDescending={() => { }}
                isAllTracksSelected={isAllTracksSelected}
                setCheckedTracks={() => { }}
                tracks={tracks}
                setIsAllTracksSelected={setIsAllTracksSelected}
                isAddFolderToProject={isAddFolderToProject}
              />
              <CustomTracksScrollingContainer
                sx={{
                  ...styles.bigScreenScrollC,
                  height: `calc(100vh - 175px - ${folders.length === 0
                    ? 0
                    : folders.length <= 8
                      ? 55
                      : 105
                    }px)`,
                }}
                ref={!isSmallScreen ? scrollContainerRef : null}
              >
                {tracks &&
                  tracks.length > 0 &&
                  tracks.map((track, i) => {
                    return (
                      <TrackListView
                        key={i}
                        index={i}
                        track={track}
                        isBoxChecked={() => false}
                        handleCheckedTrackChange={() => { }}
                        handleRefreshTracks={async () => { }}
                        handleOpenTrackVersionModal={
                          handleOpenTrackVersionModal
                        }
                        handleOpenSelectDeleteModal={() => { }}
                        handleOpenCollabsModal={async () => { }}
                        handleOpenEditTrackModal={() => { }}
                        setIsPlayButtonClicked={setIsPlayButtonClicked}
                        trackToPlay={trackToPlay}
                        setTrackToPlay={setTrackToPlay}
                        setIsTrackForPlay={setIsTrackForPlay}
                        setVersionToPlay={setVersionToPlay}
                        setAudioDetails={setAudioDetails}
                        session={undefined}
                        setInbox={() => { }}
                        handleExpandTrack={handleExpandTrack}
                        expanded={trackToExpand === track.track?.getId()}
                        handleDragStart={() => { }}
                        handleDrop={() => { }}
                        setDraggedIndex={() => { }}
                        isReorderAvailable={false}
                        handleOpenUpdateSubModal={() => { }}
                        versionToPlay={versionToPlay}
                        isAddFolderToProject={isAddFolderToProject}
                        handleApplyFilterAndSort={async () => { }}
                        shareToken={shareToken}
                        allowDownload={allowDownload}
                      />
                    );
                  })}
                {tracks.length === 0 && !isLoading && !isScrollEnabled && (
                  <Box sx={styles.emptyTracksContainer}>
                    <Typography>No tracks found!</Typography>
                  </Box>
                )}
                {isLoading && (
                  <Box
                    ref={loadingRef}
                    sx={{
                      ...styles.loadingContainer,
                      ...{ height: isSmallScreen ? "40vh" : "auto" },
                    }}
                  >
                    <CircularProgress size={50} />
                  </Box>
                )}
              </CustomTracksScrollingContainer>
            </CustomTracksListContainer>
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            <Box sx={{
              ...projectPageStyles.filesScrollContainer, height: `calc(100vh - 135px - ${folders.length === 0
                ? 0
                : folders.length <= 8
                  ? 55
                  : 100
                }px)`,
              width: window.innerWidth <= 1300 ? "69vw" : "100%",
              overflow: isSharePage ? "hidden" : "scroll",
              borderRadius: "var(--radius-xl, 12px)",
              border: `1px solid ${BORDER_COLOR}`,
            }}>
              <Box
                ref={scrollContainerRef}
                sx={{
                  ...projectPageStyles.filesSectionContainer,
                  height: `calc(100vh - 135px - ${folders.length === 0
                    ? 0
                    : folders.length <= 8
                      ? 55
                      : 100
                    }px)`,
                  border: "none"
                }}
              >
                <Box sx={{ ...projectPageStyles.titlesContainer, height: "40px" }}>
                  <Box sx={{ width: "65%" }}>Name</Box>
                  <Box sx={{ width: "30%" }}>Actions</Box>
                </Box>
                {projectFiles.map((projectFile, i) => {
                  return (
                    <ProjectFile
                      projectFile={projectFile}
                      key={i}
                      session={undefined}
                      setInbox={() => undefined}
                      getProjectFiles={getProjectFiles}
                      isFileChecked={() => false}
                      handleCheckedFileChange={() => { }}
                      setProjectFiles={setProjectFiles}
                      isAddFolderToProject={isAddFolderToProject}
                      allowDownload={allowDownload}
                    />
                  );
                })}
                {projectFiles.length === 0 && !isLoading && (
                  <Box sx={projectPageStyles.emptyMessageContainer}>
                    <Typography>No files found!</Typography>
                  </Box>
                )}
                {isLoading && (
                  <Box sx={projectPageStyles.loadingContainer}>
                    <CircularProgress />
                  </Box>
                )}
              </Box>
            </Box>
          </CustomTabPanel>
          <CustomTabPanel value={value} index={2}>
            <CustomTracksListContainer>
              <TracksTableHeadings
                filterAndSortParams={filterAndSortParams}
                handleAscendingOrDescending={() => { }}
                isAllTracksSelected={isAllTracksSelected}
                setCheckedTracks={() => { }}
                tracks={tracks}
                setIsAllTracksSelected={setIsAllTracksSelected}
                isAddFolderToProject={isAddFolderToProject}
              />
              <CustomTracksScrollingContainer
                sx={{
                  ...styles.bigScreenScrollC,
                  height: `calc(100vh - 175px - ${folders.length === 0
                    ? 0
                    : folders.length <= 8
                      ? 55
                      : 105
                    }px)`,
                }}
                ref={!isSmallScreen ? scrollContainerRef : null}
              >
                {tracks &&
                  tracks.length > 0 &&
                  tracks.map((track, i) => {
                    return (
                      <TrackListView
                        key={i}
                        index={i}
                        track={track}
                        isBoxChecked={() => false}
                        handleCheckedTrackChange={() => { }}
                        handleRefreshTracks={async () => { }}
                        handleOpenTrackVersionModal={
                          handleOpenTrackVersionModal
                        }
                        handleOpenSelectDeleteModal={() => { }}
                        handleOpenCollabsModal={async () => { }}
                        handleOpenEditTrackModal={() => { }}
                        setIsPlayButtonClicked={setIsPlayButtonClicked}
                        trackToPlay={trackToPlay}
                        setTrackToPlay={setTrackToPlay}
                        setIsTrackForPlay={setIsTrackForPlay}
                        setVersionToPlay={setVersionToPlay}
                        setAudioDetails={setAudioDetails}
                        session={undefined}
                        setInbox={() => { }}
                        handleExpandTrack={handleExpandTrack}
                        expanded={trackToExpand === track.track?.getId()}
                        handleDragStart={() => { }}
                        handleDrop={() => { }}
                        setDraggedIndex={() => { }}
                        isReorderAvailable={false}
                        handleOpenUpdateSubModal={() => { }}
                        versionToPlay={versionToPlay}
                        isAddFolderToProject={isAddFolderToProject}
                        handleApplyFilterAndSort={async () => { }}
                        shareToken={shareToken}
                        allowDownload={allowDownload}
                      />
                    );
                  })}
                {tracks.length === 0 && !isLoading && !isScrollEnabled && (
                  <Box sx={styles.emptyTracksContainer}>
                    <Typography>No tracks found!</Typography>
                  </Box>
                )}
                {isLoading && (
                  <Box
                    ref={loadingRef}
                    sx={{
                      ...styles.loadingContainer,
                      ...{ height: isSmallScreen ? "40vh" : "auto" },
                    }}
                  >
                    <CircularProgress size={50} />
                  </Box>
                )}
              </CustomTracksScrollingContainer>
            </CustomTracksListContainer>
          </CustomTabPanel>
          <AudioPlayer
            trackToPlay={trackToPlay}
            isPlayButtonClicked={isPlayButtonClicked}
            setIsPlayButtonClicked={setIsPlayButtonClicked}
            versionToPlay={versionToPlay}
            audioDetails={audioDetails}
            isTrackForPlay={isTrackForPlay}
            setTrackToPlay={setTrackToPlay}
            setVersionToPlay={setVersionToPlay}
          />
        </>
      )
      }
      {
        !share && (
          <>
            <Box sx={styles.loadingContainer}>
              {loading && !isMaxViewsReached && <CircularProgress />}
            </Box>
          </>
        )
      }
    </div >
  );
};

export default SharedFolderPage;
